import axios, { AxiosResponse } from 'axios';
import { useMemo } from 'react';
import { useUser } from '../contexts/UserContext';
import {
  DimensionReductionTechnique,
  MethodType,
  MsLevel,
  SampleIonPolarity,
  SampleOrderField,
  SampleStatus,
} from '../enums';
import {
  AnalyticConfigurationSnapshot,
  CreateAnalyticConfigurationSnapshotRequest,
  UpdateAnalyticConfigurationSnapshotRequest,
} from '../interfaces/analyticConfigurationSnapshot';
import { InterpretationChat } from '../interfaces/chat';
import { ChatHistoryMessagesResponse } from '../interfaces/chatHistory';
import { InterpretiveReport } from '../interfaces/interpretiveReport';
import {
  AgentJobCreateResponse,
  AgentJobStatus,
  Config,
  CreateConfig,
  CreateProduct,
  IdentificationInferenceResult,
  InferencePredictionType,
  InferenceResultAnalyte,
  Job,
  Model,
  Product,
  ProductViewer,
  SampleAnalyteDataType,
  SampleRepresentationData,
  SpectraResponse,
  UpdateProduct,
  Xic,
} from '../interfaces/job';
import { PaginatedResponse, SearchPaginatedResponse } from '../interfaces/misc';
import {
  JobResultsDownload,
  ResultFileDownload,
  ResultFileDownloadsResponse,
} from '../interfaces/resultFileDownloads';
import {
  AddFilesToSampleResponse,
  CreatedSample,
  DataVisibility,
  Sample,
  SampleSet,
  SampleSetQCCheck,
  TicData,
  UpdateSampleMetadataFileResponse,
  UpdateSampleMetadataResponse,
} from '../interfaces/sampleset';
import {
  LegalAcceptanceResponse,
  LegalAcceptanceStatusResponse,
  UpdateUserPayload,
} from '../interfaces/user';
import { handleStreamingDownload, saveBlob } from '../utils/download';
import {
  CREATE_CONFIG,
  CREATE_ORGANIZATION_JOB,
  CREATE_ORGANIZATION_SAMPLE,
  CREATE_ORGANIZATION_USER,
  CREATE_PLATFORM_JOB,
  CREATE_PLATFORM_SAMPLE,
  CREATE_PLATFORM_USER,
  DELETE_ORGANIZATION_SAMPLE,
  DELETE_ORGANIZATION_SAMPLE_SET,
  DELETE_PLATFORM_SAMPLE,
  DELETE_PLATFORM_SAMPLE_SET,
  PermissionString,
  READ_CONFIG,
  READ_MODEL,
  READ_ORGANIZATION_ACQUISITION,
  READ_ORGANIZATION_INFERENCE_RESULT,
  READ_ORGANIZATION_INTERPRETIVE_REPORTS,
  READ_ORGANIZATION_JOBS,
  READ_ORGANIZATION_PRODUCT,
  READ_ORGANIZATION_SAMPLE_SETS,
  READ_ORGANIZATION_SAMPLES,
  READ_ORGANIZATION_USERS,
  READ_PLATFORM_ACQUISITION,
  READ_PLATFORM_INFERENCE_RESULT,
  READ_PLATFORM_INTERPRETIVE_REPORTS,
  READ_PLATFORM_JOBS,
  READ_PLATFORM_PRODUCT,
  READ_PLATFORM_SAMPLE_SETS,
  READ_PLATFORM_SAMPLES,
  READ_PLATFORM_USERS,
  READ_PRODUCT_VIEWER,
  READ_SAMPLE_EMBEDDINGS,
  UPDATE_CONFIG,
  UPDATE_ORGANIZATION_SAMPLE,
  UPDATE_ORGANIZATION_SAMPLE_SET,
  UPDATE_ORGANIZATION_USERS,
  UPDATE_PLATFORM_SAMPLE,
  UPDATE_PLATFORM_SAMPLE_SET,
  UPDATE_PLATFORM_USERS,
  UPDATE_PRODUCT,
  UPDATE_PRODUCT_VIEWER,
} from '../utils/permissions';

/**
 * Selects a specific (non-default) file in a job's Results folder. Mirrors the
 * backend `ResultsFileType` enum; the only value today is 'protein'
 * (protein_to_peptides.parquet).
 */
export type ResultsFileType = 'protein';

export interface InterpretiveReportListOptions {
  skip?: number;
  limit?: number;
  orderField?: 'created_date' | 'last_updated';
  orderReverse?: boolean;
}

export class NovaApiClient {
  private baseUrl = '/api';
  private hasPermission: (permission: PermissionString) => boolean;

  constructor(hasPermission: (permission: PermissionString) => boolean) {
    this.hasPermission = hasPermission;
  }

  private protectLevel(
    url: string,
    platform_permission: PermissionString,
    organization_permission: PermissionString
  ) {
    if (this.hasPermission(platform_permission)) {
      url += '/platform';
    } else if (this.hasPermission(organization_permission)) {
      url += '/organization';
    }
    return url;
  }

  async getSampleSets(
    skip: number = 0,
    limit: number = 100,
    keyword?: string,
    userOwned: boolean = false
  ): Promise<any> {
    let url = this.protectLevel(
      `${this.baseUrl}/sample_sets`,
      READ_PLATFORM_SAMPLE_SETS,
      READ_ORGANIZATION_SAMPLE_SETS
    );
    try {
      const response = await axios.get(url, {
        params: {
          skip: skip,
          limit: limit,
          keyword: keyword,
          user_owned: userOwned,
        },
      });
      return response.data;
    } catch (error) {
      console.error('Error fetching sample sets:', error);
      throw error;
    }
  }

  async getSampleSetById(sampleSetId: string): Promise<SampleSet> {
    const url = this.protectLevel(
      `${this.baseUrl}/sample_sets/${sampleSetId}`,
      READ_PLATFORM_SAMPLE_SETS,
      READ_ORGANIZATION_SAMPLE_SETS
    );
    try {
      const response = await axios.get(url);
      return response.data;
    } catch (error) {
      console.error('Error fetching sample set by ID:', error);
      throw error;
    }
  }

  async getSamplesBySampleSetId(
    sampleSetId: string,
    loadQCChecks: boolean = false,
    loadAcquisitions: boolean = false
  ): Promise<Sample[]> {
    let url = this.protectLevel(
      `${this.baseUrl}/samples/sample_set/${sampleSetId}`,
      READ_PLATFORM_SAMPLES,
      READ_ORGANIZATION_SAMPLES
    );
    url += `?load_qc_checks=${loadQCChecks}&load_acquisitions=${loadAcquisitions}`;

    try {
      let skip = 0;
      const limit = 50;
      let allSamples: Sample[] = [];

      // Stop once the latest reported total is covered, which spares a request
      // when the last page is exactly full. A page shorter than the limit is
      // the end of the data either way, so a total that overshoots what the
      // endpoint can serve still terminates.
      for (;;) {
        const pageUrl = `${url}&skip=${skip}&limit=${limit}`;
        const response = await axios.get(pageUrl);
        const pageSamples: Sample[] = response.data.data;
        allSamples = allSamples.concat(pageSamples);
        if (
          pageSamples.length < limit ||
          allSamples.length >= response.data.total
        ) {
          break;
        }
        skip += limit;
      }

      return allSamples;
    } catch (error) {
      console.error('Error fetching samples by sample set ID:', error);
      throw error;
    }
  }

  async getSamples(
    skip: number = 0,
    limit: number = 100,
    keyword?: string,
    sampleSetId?: string | null,
    status?: SampleStatus[],
    orderField?: SampleOrderField,
    orderReverse?: boolean,
    userOwned?: boolean,
    loadAcquisitions?: boolean,
    polarity?: SampleIonPolarity[]
  ): Promise<PaginatedResponse<Sample>> {
    let url = this.protectLevel(
      `${this.baseUrl}/samples`,
      READ_PLATFORM_SAMPLES,
      READ_ORGANIZATION_SAMPLES
    );
    const response = await axios.get(url, {
      params: {
        skip: skip,
        limit: limit,
        keyword: keyword,
        sample_set_id: sampleSetId,
        order_field: orderField,
        order_reverse: orderReverse,
        user_owned: userOwned,
        load_acquisitions: loadAcquisitions,
        status: status,
        polarity: polarity,
      },
      paramsSerializer: {
        indexes: null,
      },
    });
    return response.data;
  }

  async getPaginatedSamplesBySampleSet(
    sampleSetId: string,
    skip: number = 0,
    limit: number = 100,
    keyword?: string,
    status?: SampleStatus[],
    orderField?: SampleOrderField,
    orderReverse?: boolean,
    polarity?: SampleIonPolarity[],
    msLevel?: MsLevel[],
    metadataFilters?: Record<string, string[]>,
    createdFrom?: string,
    createdTo?: string,
    failedQcChecks?: string[]
  ): Promise<PaginatedResponse<Sample>> {
    const url = this.protectLevel(
      `${this.baseUrl}/samples`,
      READ_PLATFORM_SAMPLES,
      READ_ORGANIZATION_SAMPLES
    );
    // Metadata filters are sent as repeated `meta.<key>=<value>` query params
    const metadataParams: Record<string, string[]> = {};
    Object.entries(metadataFilters ?? {}).map(([key, values]) => {
      if (values.length > 0) {
        metadataParams[`meta.${key}`] = values;
      }
    });
    const response = await axios.get(url, {
      params: {
        sample_set_id: sampleSetId,
        skip,
        limit,
        keyword,
        order_field: orderField,
        order_reverse: orderReverse,
        status,
        failed_qc_check: failedQcChecks,
        polarity,
        ms_level: msLevel,
        created_from: createdFrom,
        created_to: createdTo,
        ...metadataParams,
      },
      paramsSerializer: {
        indexes: null,
      },
    });
    return response.data;
  }

  async getSampleById(
    sampleId: string,
    loadQCChecks: boolean = false,
    loadResults: boolean = false
  ): Promise<any> {
    let url = this.protectLevel(
      `${this.baseUrl}/samples/${sampleId}`,
      READ_PLATFORM_SAMPLES,
      READ_ORGANIZATION_SAMPLES
    );
    url += `?load_qc_checks=${loadQCChecks}&load_results=${loadResults}`;
    try {
      const response = await axios.get(url);
      return response.data;
    } catch (error) {
      console.error('Error fetching sample by ID:', error);
      throw error;
    }
  }

  async getSampleMismatches(sampleSetId: string): Promise<any> {
    const url = this.protectLevel(
      `${this.baseUrl}/sample_sets/${sampleSetId}/sample_metadata_mismatches`,
      READ_PLATFORM_SAMPLES,
      READ_ORGANIZATION_SAMPLES
    );
    try {
      const response = await axios.get(url);
      return response.data;
    } catch (error) {
      console.error('Error fetching sample mismatches:', error);
      throw error;
    }
  }

  async getSampleNamesBySampleSetId(sampleSetId: string): Promise<string[]> {
    const url = this.protectLevel(
      `${this.baseUrl}/samples/sample_set/${sampleSetId}/sample_names`,
      READ_PLATFORM_SAMPLES,
      READ_ORGANIZATION_SAMPLES
    );
    try {
      const response = await axios.get(url);
      return response.data;
    } catch (error) {
      console.error('Error fetching sample names by sample set ID:', error);
      throw error;
    }
  }

  async createSampleMetadata(
    sampleId: string,
    metadata: Record<string, any>
  ): Promise<any> {
    let url = this.protectLevel(
      `${this.baseUrl}/samples/${sampleId}/metadata`,
      UPDATE_PLATFORM_SAMPLE,
      UPDATE_ORGANIZATION_SAMPLE
    );
    url += `?type=user`;
    try {
      const response = await axios.put(url, metadata);
      return response.data;
    } catch (error) {
      console.error('Error creating sample metadata:', error);
      throw error;
    }
  }

  async invokeMetadataAgent(
    sampleSetId: string
  ): Promise<AgentJobCreateResponse> {
    let url = `${this.baseUrl}/agents/metadata_agent/sample_set/${sampleSetId}`;
    url = this.protectLevel(
      url,
      UPDATE_PLATFORM_SAMPLE,
      UPDATE_ORGANIZATION_SAMPLE
    );

    try {
      const response = await axios.post<AgentJobCreateResponse>(url);
      return response.data;
    } catch (error) {
      console.error('Error organizing sample set files:', error);
      throw error;
    }
  }

  async getAgentJobStatus(jobId: string): Promise<AgentJobStatus> {
    const url = `${this.baseUrl}/agents/agent_jobs/${jobId}`;

    try {
      const response = await axios.get<AgentJobStatus>(url);
      return response.data;
    } catch (error) {
      console.error('Error fetching agent job status:', error);
      throw error;
    }
  }

  async createSampleSet(
    name: string,
    method?: string,
    msLevel?: string
  ): Promise<SampleSet> {
    const url = `${this.baseUrl}/sample_sets`;
    try {
      const payload: { set_name: string; method?: string; ms_level?: string } =
        { set_name: name };
      if (method) {
        payload.method = method;
      }
      if (msLevel) {
        payload.ms_level = msLevel;
      }
      const response = await axios.post(url, payload);
      return response.data;
    } catch (error) {
      console.error('Error creating sample set:', error);
      throw error;
    }
  }

  async renameSamplesUserMetadataColumn(
    sampleSetId: string,
    oldName: string,
    newName: string
  ): Promise<any> {
    let url = this.protectLevel(
      `${this.baseUrl}/samples/sample_set/${sampleSetId}/metadata/rename`,
      UPDATE_PLATFORM_SAMPLE_SET,
      UPDATE_ORGANIZATION_SAMPLE_SET
    );
    url += `?old_name=${oldName}&new_name=${newName}`;
    try {
      const response = await axios.put(url);
      return response.data;
    } catch (error) {
      console.error('Error renaming sample user metadata column:', error);
      throw error;
    }
  }

  async deleteSampleUserMetadataColumnFromSampleset(
    metadataField: string,
    sampleSetId: string
  ): Promise<any> {
    let url = this.protectLevel(
      `${this.baseUrl}/samples/sample_set/${sampleSetId}/metadata`,
      UPDATE_PLATFORM_SAMPLE_SET,
      UPDATE_ORGANIZATION_SAMPLE_SET
    );
    url += `?metadata_field=${metadataField}`;
    try {
      const response = await axios.delete(url);
      return response.data;
    } catch (error) {
      console.error('Error deleting sample set metadata:', error);
      throw error;
    }
  }

  async configureSampleSetMethod(
    sampleSetId: string,
    method: MethodType
  ): Promise<any> {
    const url = this.protectLevel(
      `${this.baseUrl}/sample_sets/${sampleSetId}/method`,
      UPDATE_PLATFORM_SAMPLE_SET,
      UPDATE_ORGANIZATION_SAMPLE_SET
    );
    try {
      const response = await axios.post(url, {
        method: method.toString().toLowerCase(),
      });
      return response.data;
    } catch (error) {
      console.error('Error configuring sample set method:', error);
      throw error;
    }
  }

  async deleteSampleSet(sampleSetId: string): Promise<any> {
    const url = this.protectLevel(
      `${this.baseUrl}/sample_sets/${sampleSetId}`,
      DELETE_PLATFORM_SAMPLE_SET,
      DELETE_ORGANIZATION_SAMPLE_SET
    );
    try {
      const response = await axios.delete(url);
      return response.data;
    } catch (error) {
      console.error('Error deleting sample set:', error);
      throw error;
    }
  }

  async shareSampleSetWithOrganization(
    sampleSetId: string,
    organizationId: string
  ): Promise<SampleSet> {
    const url = `${this.baseUrl}/sample_sets/${sampleSetId}/share/organization/${organizationId}`;
    try {
      const response = await axios.put(url);
      return response.data;
    } catch (error) {
      console.error('Error sharing sample set with organization:', error);
      throw error;
    }
  }

  async unshareSampleSetWithOrganization(
    sampleSetId: string,
    organizationId: string
  ): Promise<SampleSet> {
    const url = `${this.baseUrl}/sample_sets/${sampleSetId}/share/organization/${organizationId}`;
    try {
      const response = await axios.delete(url);
      return response.data;
    } catch (error) {
      console.error('Error unsharing sample set with organization:', error);
      throw error;
    }
  }

  async updateSampleSetDataVisibility(
    sampleSetId: string,
    dataVisibility: DataVisibility
  ): Promise<SampleSet> {
    const url = `${this.baseUrl}/sample_sets/${sampleSetId}/data_visibility`;
    try {
      const response = await axios.put(url, {
        data_visibility: dataVisibility,
      });
      return response.data;
    } catch (error) {
      console.error('Error updating sample set data visibility:', error);
      throw error;
    }
  }

  async deleteSample(sampleId: string): Promise<any> {
    const url = this.protectLevel(
      `${this.baseUrl}/samples/${sampleId}`,
      DELETE_PLATFORM_SAMPLE,
      DELETE_ORGANIZATION_SAMPLE
    );
    try {
      const response = await axios.delete(url);
      return response.data;
    } catch (error) {
      console.error('Error deleting sample:', error);
      throw error;
    }
  }

  async deleteSamples(
    sampleSetId: string,
    sampleIds: string[]
  ): Promise<{ deleted: string[]; skipped: string[] }> {
    const url = `${this.baseUrl}/samples/sample_set/${sampleSetId}/delete`;
    try {
      const response = await axios.post(url, { sample_ids: sampleIds });
      return response.data;
    } catch (error) {
      console.error('Error deleting samples:', error);
      throw error;
    }
  }

  async markSampleUploaded(sampleId: string): Promise<Sample> {
    const url = `${this.baseUrl}/samples/${sampleId}/uploaded`;
    try {
      const response = await axios.put(url);
      return response.data;
    } catch (error) {
      console.error('Error marking sample uploaded:', error);
      throw error;
    }
  }

  async getSampleSetUrl(sampleSetId: string): Promise<any> {
    const url = `${this.baseUrl}/sample_sets/${sampleSetId}/url`;
    try {
      const response = await axios.get(url);
      return response.data;
    } catch (error) {
      console.error('Error fetching sample set URL:', error);
      throw error;
    }
  }

  async createSample(data: {
    sample_name: string;
    sample_set_id: string;
    files: { file_name: string; file_size_bytes: number }[];
  }): Promise<CreatedSample> {
    const url = this.protectLevel(
      `${this.baseUrl}/samples`,
      CREATE_PLATFORM_SAMPLE,
      CREATE_ORGANIZATION_SAMPLE
    );
    try {
      const response = await axios.post(url, data);
      return response.data;
    } catch (error) {
      console.error('Error creating sample:', error);
      throw error;
    }
  }

  async addFilesToSample(
    sampleId: string,
    files: { file_name: string; file_size_bytes: number }[]
  ): Promise<AddFilesToSampleResponse> {
    const url = this.protectLevel(
      `${this.baseUrl}/samples/${sampleId}/files`,
      CREATE_PLATFORM_SAMPLE,
      CREATE_ORGANIZATION_SAMPLE
    );
    try {
      const response = await axios.post(url, { files });
      return response.data;
    } catch (error) {
      console.error('Error adding files to sample:', error);
      throw error;
    }
  }

  async getJobs(
    skip: number = 0,
    limit: number = 10,
    sampleSetId?: string | null
  ): Promise<SearchPaginatedResponse<Job>> {
    let url = this.protectLevel(
      `${this.baseUrl}/jobs`,
      READ_PLATFORM_JOBS,
      READ_ORGANIZATION_JOBS
    );
    url += `?skip=${skip}&limit=${limit}&order_field=created_date&order_reverse=true`;
    if (sampleSetId) {
      url += `&sample_set_id=${sampleSetId}`;
    }
    try {
      const response = await axios.get<SearchPaginatedResponse<Job>>(url);
      return response.data;
    } catch (error) {
      console.error('Error fetching jobs:', error);
      throw error;
    }
  }

  async getJobById(jobId: string): Promise<Job> {
    const url = this.protectLevel(
      `${this.baseUrl}/jobs/${jobId}`,
      READ_PLATFORM_JOBS,
      READ_ORGANIZATION_JOBS
    );
    try {
      const response = await axios.get(url);
      return response.data;
    } catch (error) {
      console.error('Error fetching job by ID:', error);
      throw error;
    }
  }

  async getInferencePredictionsByJobIdGrouping(
    jobId: string
  ): Promise<InferencePredictionType[]> {
    let url = this.protectLevel(
      `${this.baseUrl}/inference_results/job/${jobId}/prediction_types`,
      READ_PLATFORM_INFERENCE_RESULT,
      READ_ORGANIZATION_INFERENCE_RESULT
    );
    try {
      const response = await axios.get(url);
      return response.data;
    } catch (error) {
      console.error(
        'Error fetching inference predictions by job ID and prediction:',
        error
      );
      throw error;
    }
  }

  async getInferenceResults(
    jobId: string,
    skip: number,
    limit: number,
    keyword: string | null,
    orderField: string | null,
    orderReverse: boolean,
    includeZeros: boolean,
    prediction: InferencePredictionType,
    sampleId?: string | null,
    analyteId?: string | null,
    inRangeThreshold?: number | null,
    r2Threshold?: number | null,
    signal?: AbortSignal
  ): Promise<PaginatedResponse<InferenceResultAnalyte>> {
    let url = this.protectLevel(
      `${this.baseUrl}/inference_results/job/${jobId}`,
      READ_PLATFORM_INFERENCE_RESULT,
      READ_ORGANIZATION_INFERENCE_RESULT
    );
    url += `?prediction_name=${prediction.prediction_name}&prediction_type=${prediction.prediction_type}&skip=${skip}&limit=${limit}&order_field=${orderField}&order_reverse=${orderReverse}&include_zeros=${includeZeros}`;
    if (keyword) {
      url += `&keyword=${keyword}`;
    }
    if (sampleId) {
      url += `&sample_id=${sampleId}`;
    }
    if (analyteId) {
      url += `&analyte_id=${analyteId}`;
    }
    if (inRangeThreshold !== null && inRangeThreshold !== undefined) {
      url += `&in_range_threshold=${inRangeThreshold}`;
    }
    if (r2Threshold !== null && r2Threshold !== undefined) {
      url += `&r2_threshold=${r2Threshold}`;
    }
    try {
      const response = await axios.get(url, { signal });
      return response.data;
    } catch (error) {
      // A caller that drops the request is not a failure; only real failures
      // belong in the console and in the error telemetry it feeds.
      if (axios.isCancel(error)) {
        throw error;
      }
      console.error(
        'Error fetching inference results by job ID and prediction type:',
        error
      );
      throw error;
    }
  }

  async getIdentificationInferenceResults(
    jobId: string
  ): Promise<IdentificationInferenceResult[]> {
    const url = this.protectLevel(
      `${this.baseUrl}/inference_results/identifications/job/${jobId}`,
      READ_PLATFORM_INFERENCE_RESULT,
      READ_ORGANIZATION_INFERENCE_RESULT
    );

    try {
      const response = await axios.get<IdentificationInferenceResult[]>(url);
      return response.data;
    } catch (error) {
      console.error('Error fetching identification inference results:', error);
      throw error;
    }
  }

  async saveInferenceResultFilters(
    jobId: string,
    inRangeThreshold?: number | null,
    r2Threshold?: number | null,
    includeZeros?: boolean | null
  ): Promise<void> {
    const filterData = {
      in_range_threshold: inRangeThreshold,
      r2_threshold: r2Threshold,
      include_zeros: includeZeros,
    };

    const url = this.protectLevel(
      `${this.baseUrl}/jobs/${jobId}/filters`,
      READ_PLATFORM_JOBS,
      READ_ORGANIZATION_JOBS
    );
    await axios.put(url, filterData);
  }

  async getInferenceResultFilters(jobId: string): Promise<{
    in_range_threshold?: number | null;
    r2_threshold?: number | null;
    include_zeros?: boolean | null;
  }> {
    const url = this.protectLevel(
      `${this.baseUrl}/jobs/${jobId}/filters`,
      READ_PLATFORM_JOBS,
      READ_ORGANIZATION_JOBS
    );
    const response = await axios.get(url);
    return response.data;
  }

  async getQcChecksBySampleSetId(
    sampleSetId: string
  ): Promise<Array<SampleSetQCCheck>> {
    const url = `${this.baseUrl}/qc_checks/sample_set/${sampleSetId}`;
    try {
      const response = await axios.get(url);
      return response.data;
    } catch (error) {
      console.error('Error fetching QC checks by sample set ID:', error);
      throw error;
    }
  }

  async getPeakAreasPlotByJobIdAnalyteId(
    jobId: string,
    analyteId: string
  ): Promise<SampleAnalyteDataType[]> {
    let url = `${this.baseUrl}/inference_results/plots/peak_areas/job/${jobId}/analyte/${analyteId}`;
    try {
      let skip = 0;
      const limit = 50;
      let plotData: SampleAnalyteDataType[] = [];

      while (true) {
        const paginatedUrl = url + `?skip=${skip}&limit=${limit}`;
        console.warn(`Fetching data from URL: ${paginatedUrl}`);
        const apiResponse = await axios.get(paginatedUrl);
        const response = apiResponse.data;

        plotData = plotData.concat(response.data);

        if (response.data.length < limit) {
          break; // Got less results than limit, must be the last page
        }

        skip += limit;
      }

      return plotData;
    } catch (error) {
      console.error(
        'Error fetching inference results by job ID and prediction type:',
        error
      );
      throw error;
    }
  }

  async getEmbeddingsByJobId(
    jobId: string,
    reduction: DimensionReductionTechnique,
    signal?: AbortSignal
  ): Promise<SampleRepresentationData[]> {
    let url = `${this.baseUrl}/inference_results/plots/job/${jobId}/sample_embeddings/pyxis_concentration`;
    url = this.protectLevel(
      url,
      READ_PLATFORM_INFERENCE_RESULT,
      READ_ORGANIZATION_INFERENCE_RESULT
    );
    url += `?reduction=${reduction.toLowerCase()}`;

    try {
      const response = await axios.get(url, { signal });
      return response.data;
    } catch (error) {
      // A caller that drops the request is not a failure; only real failures
      // belong in the console and in the error telemetry it feeds.
      if (axios.isCancel(error)) {
        throw error;
      }
      console.error('Error fetching sample embeddings by job ID:', error);
      throw error;
    }
  }

  async getInferenceResultsByJobIdAnalyteIdPrediction(
    jobId: string,
    analyteId: string,
    prediction: InferencePredictionType,
    sampleId?: string | null
  ): Promise<any> {
    let url = `${this.baseUrl}/inference_results/job/${jobId}/analyte/${analyteId}`;
    url = this.protectLevel(
      url,
      READ_PLATFORM_INFERENCE_RESULT,
      READ_ORGANIZATION_INFERENCE_RESULT
    );
    url += `?prediction_name=${prediction.prediction_name}&prediction_type=${prediction.prediction_type}`;

    if (sampleId) {
      url += `&sample_id=${sampleId}`;
    }
    try {
      const response = await axios.get(url);
      return response.data;
    } catch (error) {
      console.error(
        'Error fetching inference results by job ID, analyte ID, and prediction type:',
        error
      );
      throw error;
    }
  }

  async getSampleUmapByJobId(jobId: string): Promise<any> {
    let url = `${this.baseUrl}/inference_results/plots/job/${jobId}/sample_representations/identification`;
    url = this.protectLevel(
      url,
      READ_PLATFORM_INFERENCE_RESULT,
      READ_ORGANIZATION_INFERENCE_RESULT
    );

    try {
      const response = await axios.get(url);
      return response.data;
    } catch (error) {
      console.error(
        'Error fetching sample identification representations:',
        error
      );
      throw error;
    }
  }

  async getSampleSetMetadataValues(
    sampleSetId: string
  ): Promise<{ values: Record<string, string[]> }> {
    const url = this.protectLevel(
      `${this.baseUrl}/sample_sets/${sampleSetId}/metadata_values`,
      READ_PLATFORM_SAMPLES,
      READ_ORGANIZATION_SAMPLES
    );
    const response = await axios.get<{ values: Record<string, string[]> }>(url);
    return response.data;
  }

  async postJob(payload: {
    product_id: string;
    sample_set_id: string;
    sample_ids: string[];
  }): Promise<Job> {
    let url = this.protectLevel(
      `${this.baseUrl}/jobs`,
      CREATE_PLATFORM_JOB,
      CREATE_ORGANIZATION_JOB
    );
    try {
      const response = await axios.post(url, payload);
      return response.data;
    } catch (error) {
      console.error('Error posting job:', error);
      throw error;
    }
  }

  async postJobWithConfig(payload: {
    sample_set_id: string;
    config_id: string;
    sample_ids: string[];
  }): Promise<Job> {
    const url = `${this.baseUrl}/jobs/config`;
    try {
      const response = await axios.post(url, payload);
      return response.data;
    } catch (error) {
      console.error('Error posting job with config:', error);
      throw error;
    }
  }

  async getOrganizations(
    skip: number = 0,
    limit: number = 10,
    keyword?: string
  ): Promise<any> {
    let url = `${this.baseUrl}/organizations`;
    url += `?skip=${skip}&limit=${limit}`;
    if (keyword) {
      url += `&keyword=${keyword}`;
    }
    try {
      const response = await axios.get(url);
      return response.data;
    } catch (error) {
      console.error('Error fetching organizations:', error);
      throw error;
    }
  }

  async getOrganizationById(organizationId: string): Promise<any> {
    const url = `${this.baseUrl}/organizations/${organizationId}`;
    try {
      const response = await axios.get(url);
      return response.data;
    } catch (error) {
      console.error('Error fetching organization:', error);
      throw error;
    }
  }

  async getOrganizationNames(): Promise<any> {
    const url = `${this.baseUrl}/organizations/names`;
    try {
      const response = await axios.get(url);
      return response.data;
    } catch (error) {
      console.error('Error fetching organization names:', error);
      throw error;
    }
  }

  async updateOrganization(
    organizationId: string,
    organizationData: any
  ): Promise<any> {
    const url = `${this.baseUrl}/organizations/${organizationId}`;
    try {
      const response = await axios.put(url, organizationData);
      return response.data;
    } catch (error) {
      console.error('Error updating organization:', error);
      throw error;
    }
  }

  async createOrganization(organizationData: any): Promise<any> {
    const url = `${this.baseUrl}/organizations`;
    try {
      const response = await axios.post(url, organizationData);
      return response.data;
    } catch (error) {
      console.error('Error creating organization:', error);
      throw error;
    }
  }

  async getUsers(
    organizationId?: string,
    skip: number = 0,
    limit: number = 10,
    keyword?: string
  ): Promise<any> {
    let url = this.protectLevel(
      `${this.baseUrl}/users`,
      READ_PLATFORM_USERS,
      READ_ORGANIZATION_USERS
    );
    url += `?skip=${skip}&limit=${limit}`;
    if (organizationId) {
      url += `&org=${organizationId}`;
    }
    if (keyword) {
      url += `&keyword=${keyword}`;
    }
    try {
      const response = await axios.get(url);
      return response.data;
    } catch (error) {
      console.error('Error fetching users:', error);
      throw error;
    }
  }

  async updateUser(userData: UpdateUserPayload): Promise<any> {
    const url = this.protectLevel(
      `${this.baseUrl}/users`,
      UPDATE_PLATFORM_USERS,
      UPDATE_ORGANIZATION_USERS
    );
    try {
      const response = await axios.put(url, userData);
      return response.data;
    } catch (error) {
      console.error('Error updating user:', error);
      throw error;
    }
  }

  async clearWhatsNewContent(): Promise<any> {
    const url = `${this.baseUrl}/users/whats-new`;
    try {
      const response = await axios.put(url);
      return response.data;
    } catch (error) {
      console.error('Error updating user whats new content:', error);
      throw error;
    }
  }

  async updateUserStatus(userId: string, blocked: boolean): Promise<any> {
    const url = this.protectLevel(
      `${this.baseUrl}/users/status`,
      UPDATE_PLATFORM_USERS,
      UPDATE_ORGANIZATION_USERS
    );
    try {
      const response = await axios.put(url, { id: userId, enabled: !blocked });
      return response.data;
    } catch (error) {
      console.error('Error updating user status:', error);
      throw error;
    }
  }

  async checkLegalAcceptanceStatus(): Promise<LegalAcceptanceStatusResponse> {
    const url = `${this.baseUrl}/users/legal-acceptance`;
    try {
      const response = await axios.get(url);
      return response.data;
    } catch (error) {
      console.error('Error checking legal acceptance status:', error);
      throw error;
    }
  }

  async acceptLegalDocuments(): Promise<LegalAcceptanceResponse> {
    const url = `${this.baseUrl}/users/legal-acceptance`;
    try {
      const response = await axios.post(url);
      return response.data;
    } catch (error) {
      console.error('Error accepting legal documents:', error);
      throw error;
    }
  }

  async downloadInferenceResults(
    jobId: string,
    showProprietaryReportInfo: boolean = false,
    colorCode: boolean,
    includesWideTable: boolean,
    includesLongTable: boolean,
    shouldImpute: boolean,
    showOutOfRange: boolean,
    sampleExclusions: string[] = [],
    analyteExclusions: string[] = []
  ): Promise<AxiosResponse<any, any>> {
    const url = this.protectLevel(
      `${this.baseUrl}/jobs/${jobId}/results`,
      READ_PLATFORM_JOBS,
      READ_ORGANIZATION_JOBS
    );
    const payload = {
      show_proprietary_report_info: showProprietaryReportInfo,
      color_code: colorCode,
      includes_wide_table: includesWideTable,
      includes_long_table: includesLongTable,
      should_impute: shouldImpute,
      show_out_of_range: showOutOfRange,
      regenerate: false,
      timeout_minutes: 15,
      sample_exclusions: sampleExclusions,
      analyte_exclusions: analyteExclusions,
    };

    try {
      console.log(
        'Making request to download inference results for job:',
        jobId
      );
      const response = await axios.post(url, payload, {
        responseType: 'blob',
        headers: {
          Accept:
            'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
        },
      });
      console.log('Received streaming response with status:', response.status);

      // Check if it's a JSON response (status message)
      const contentType = response.headers['content-type'] as
        | string
        | undefined;
      if (contentType && contentType.includes('application/json')) {
        // Convert blob to text to read JSON
        const blob = response.data;
        const textData = await new Promise<string>((resolve) => {
          const reader = new FileReader();
          reader.onload = () => resolve(reader.result as string);
          reader.readAsText(blob);
        });

        const jsonData = JSON.parse(textData);
        console.log('Received JSON response:', jsonData);

        if (response.status === 202) {
          console.log('Report is still generating');
        } else if (response.status === 201) {
          console.log('Report generation started');
        }

        return response;
      }

      // Handle streaming file response using utility function
      handleStreamingDownload(response, 'inference-results.xlsx');

      return response;
    } catch (error) {
      console.error('Error downloading inference results:', error);
      if (axios.isAxiosError(error)) {
        console.error('Response status:', error.response?.status);
        console.error('Response data:', error.response?.data);
      }
      throw error;
    }
  }

  async downloadResults(jobId: string): Promise<AxiosResponse<any, any>> {
    const url = this.protectLevel(
      `${this.baseUrl}/jobs/${jobId}/results`,
      READ_PLATFORM_JOBS,
      READ_ORGANIZATION_JOBS
    );

    try {
      console.log('Making request to download results for job:', jobId);

      // Make request with blob type and redirect handling
      const response = await axios.post(
        url,
        {},
        {
          responseType: 'blob',
          maxRedirects: 0,
          validateStatus: (status) => status < 400, // Accept 2xx and 3xx
          headers: {
            Accept:
              'application/zip, application/vnd.openxmlformats-officedocument.spreadsheetml.sheet, application/json',
          },
        }
      );

      console.log('Received response with status:', response.status);

      if (response.status === 301 || response.status === 302) {
        const redirectUrl = response.headers.location;
        if (redirectUrl) {
          console.log('Opening redirect URL:', redirectUrl);
          window.open(redirectUrl, '_blank', 'noopener,noreferrer');
          return response;
        }
      }

      // Check if it's a JSON response (status messages)
      const contentType = response.headers['content-type'] as
        | string
        | undefined;
      if (contentType && contentType.includes('application/json')) {
        // Convert blob to text to read JSON
        const blob = response.data;
        const textData = await new Promise<string>((resolve) => {
          const reader = new FileReader();
          reader.onload = () => resolve(reader.result as string);
          reader.readAsText(blob);
        });

        const jsonData = JSON.parse(textData);
        console.log('Received JSON response:', jsonData);

        // Return a modified response with the parsed JSON data
        return {
          ...response,
          data: jsonData,
        };
      }

      // Handle streaming file download
      if (response.data) {
        handleStreamingDownload(response, 'results-download');
      }

      return response;
    } catch (error) {
      console.error('Error downloading results:', error);
      if (axios.isAxiosError(error)) {
        // Handle redirect errors (axios treats 3xx as errors when maxRedirects: 0)
        if (error.response?.status === 301 || error.response?.status === 302) {
          const redirectUrl = error.response.headers.location;
          if (redirectUrl) {
            console.log('Opening redirect URL from error:', redirectUrl);
            window.open(redirectUrl, '_blank', 'noopener,noreferrer');
            return error.response;
          }
        }
      }
      throw error;
    }
  }

  async downloadMzml(acquisitionId: string): Promise<any> {
    // Build the correct endpoint to get the presigned URL based on user permissions
    let url = `${this.baseUrl}/acquisitions/${acquisitionId}/mzml/url`;
    url = this.protectLevel(
      url,
      READ_PLATFORM_ACQUISITION,
      READ_ORGANIZATION_ACQUISITION
    );
    try {
      console.log(
        'Getting download URL for mzML acquisition:',
        acquisitionId,
        'URL:',
        url
      );

      const response = await axios.get(url);
      const downloadUrl = response.data.download_url;

      if (downloadUrl) {
        console.log('Navigating to CloudFront URL:', downloadUrl);
        window.location.href = downloadUrl;
      } else {
        throw new Error('No download URL returned from server');
      }

      return response;
    } catch (error) {
      console.error('Error downloading mzML:', error);
      throw error;
    }
  }

  async downloadRaw(acquisitionId: string): Promise<any> {
    // Build the correct endpoint to get the presigned URL based on user permissions
    let url = `${this.baseUrl}/acquisitions/${acquisitionId}/raw/url`;
    url = this.protectLevel(
      url,
      READ_PLATFORM_ACQUISITION,
      READ_ORGANIZATION_ACQUISITION
    );
    try {
      console.log(
        'Getting download URL for RAW acquisition:',
        acquisitionId,
        'URL:',
        url
      );

      const response = await axios.get(url);
      const downloadUrl = response.data.download_url;

      if (downloadUrl) {
        console.log('Navigating to CloudFront URL:', downloadUrl);
        window.location.href = downloadUrl;
      } else {
        throw new Error('No download URL returned from server');
      }

      return response;
    } catch (error) {
      console.error('Error downloading RAW file:', error);
      throw error;
    }
  }

  /**
   * Download a job's sample embeddings file (LSM-MS1 jobs get the outputs ZIP).
   *
   * The endpoint 307-redirects to a CloudFront presigned URL; axios follows
   * the redirect and returns the file body as a blob. The filename comes from
   * the redirect target's path since the presigned response carries no
   * Content-Disposition header.
   */
  async downloadSampleEmbeddings(
    jobId: string
  ): Promise<{ blob: Blob; filename: string }> {
    // Only internal users with read:sample_embeddings permission can download
    if (!this.hasPermission(READ_SAMPLE_EMBEDDINGS)) {
      throw new Error('Unauthorized attempt to download sample embeddings');
    }

    const url = `${this.baseUrl}/jobs/${jobId}/sample-embeddings/platform`;

    try {
      const response = await axios.get(url, {
        responseType: 'blob',
      });

      let filename = `sample_embeddings_${jobId}.parquet`;
      const finalUrl: string | undefined = response.request?.responseURL;
      if (finalUrl) {
        const basename = new URL(finalUrl).pathname.split('/').pop();
        if (basename) {
          filename = decodeURIComponent(basename);
        }
      }

      // Return blob and filename for component to handle download
      const blob = new Blob([response.data], {
        type: 'application/octet-stream',
      });

      return { blob, filename };
    } catch (error) {
      console.error('Error downloading sample embeddings:', error);
      throw error;
    }
  }

  /**
   * Download a job's results file, or list its result files, depending on
   * the job's result type — the endpoint's response shape differs:
   *
   * - Fixed-file result types 307-redirect to a CloudFront presigned URL;
   *   axios follows the redirect and the body is the file itself
   *   (`{ type: 'file', blob }`). With no `fileType` that's the
   *   pipeline-default file (peptide_identifications.parquet for peptide-id
   *   jobs); a `fileType` selects another Results file (e.g. 'protein').
   * - MWMINE_FEATURES jobs answer 200 with a JSON listing of presigned
   *   attachment URLs (`{ type: 'fileListing', files }`) — their file set
   *   isn't known ahead of time.
   */
  async downloadResultsFile(
    jobId: string,
    fileType?: ResultsFileType
  ): Promise<JobResultsDownload> {
    let url = this.protectLevel(
      `${this.baseUrl}/inference_results/job/${jobId}/results`,
      READ_PLATFORM_INFERENCE_RESULT,
      READ_ORGANIZATION_INFERENCE_RESULT
    );
    const response = await axios.get(url, {
      responseType: 'blob',
      params: fileType ? { file_type: fileType } : {},
    });
    const contentType = response.headers['content-type'] as string | undefined;
    if (contentType && contentType.includes('application/json')) {
      const listing = JSON.parse(
        await (response.data as Blob).text()
      ) as ResultFileDownloadsResponse;
      return { type: 'fileListing', files: listing.files };
    }
    return {
      type: 'file',
      blob: new Blob([response.data], { type: 'application/octet-stream' }),
    };
  }

  async createUser(userData: {
    email: string;
    first_name: string;
    last_name: string;
    org: string;
  }): Promise<any> {
    const url = this.protectLevel(
      `${this.baseUrl}/users`,
      CREATE_PLATFORM_USER,
      CREATE_ORGANIZATION_USER
    );
    try {
      const response = await axios.post(url, userData);
      return response.data;
    } catch (error) {
      console.error('Error creating user:', error);
      throw error;
    }
  }

  async getXic(
    jobId: string,
    analyteId: string,
    adduct: string | null = null
  ): Promise<Record<string, Xic[]>> {
    const url = this.protectLevel(
      `${this.baseUrl}/inference_results/plots/job/${jobId}/analyte/${analyteId}/xic/${adduct}`,
      READ_PLATFORM_JOBS,
      READ_ORGANIZATION_JOBS
    );
    try {
      const response = await axios.get(url);
      return response.data;
    } catch (error) {
      console.error('Error fetching XIC data:', error);
      throw error;
    }
  }

  async getTicForSample(sampleId: string): Promise<TicData> {
    const url = this.protectLevel(
      `${this.baseUrl}/samples/${sampleId}/tic`,
      READ_PLATFORM_SAMPLES,
      READ_ORGANIZATION_SAMPLES
    );

    try {
      const response = await axios.get<TicData>(url);
      return response.data;
    } catch (error) {
      console.error('Error fetching TIC data for sample:', error);
      throw error;
    }
  }

  async getSampleSetMethod(sampleSetId: string): Promise<MethodType> {
    const url = this.protectLevel(
      `${this.baseUrl}/sample_sets/${sampleSetId}/method`,
      READ_PLATFORM_SAMPLE_SETS,
      READ_ORGANIZATION_SAMPLE_SETS
    );
    try {
      const response = await axios.get(url);
      return response.data.method as MethodType;
    } catch (error) {
      console.error('Error fetching sample set method:', error);
      throw error;
    }
  }

  async getSampleSetAcquisitionInfo(sampleSetId: string): Promise<{
    methods: string[];
    ms_levels: string[];
    has_matching_polarity: boolean;
    mismatched_polarity_samples: string[];
  }> {
    const url = this.protectLevel(
      `${this.baseUrl}/sample_sets/${sampleSetId}/acquisition_info`,
      READ_PLATFORM_SAMPLE_SETS,
      READ_ORGANIZATION_SAMPLE_SETS
    );
    try {
      const response = await axios.get(url);
      return {
        methods: response.data.methods,
        ms_levels: response.data.ms_levels,
        has_matching_polarity: response.data.has_matching_polarity ?? true,
        mismatched_polarity_samples:
          response.data.mismatched_polarity_samples ?? [],
      };
    } catch (error) {
      console.error('Error fetching sample set acquisition info:', error);
      throw error;
    }
  }

  async getProducts(active: boolean): Promise<Product[]> {
    const url = this.protectLevel(
      `${this.baseUrl}/products`,
      READ_PLATFORM_PRODUCT,
      READ_ORGANIZATION_PRODUCT
    );
    try {
      const response = await axios.get(url, {
        params: { active_only: active },
      });
      return response.data;
    } catch (error) {
      console.error('Error fetching products:', error);
      throw error;
    }
  }

  async updateProduct(product: UpdateProduct): Promise<Product[]> {
    if (!this.hasPermission(UPDATE_PRODUCT)) {
      throw new Error('Permission denied: UPDATE_PRODUCT');
    }

    const url = `${this.baseUrl}/products/${product.id}`;
    try {
      const response = await axios.put(url, {
        internal_name: product.internal_name,
        display_name: product.display_name,
        description: product.description,
      });
      return response.data;
    } catch (error) {
      console.error('Error updating product:', error);
      throw error;
    }
  }

  async createProduct(product: CreateProduct): Promise<Product> {
    if (!this.hasPermission(UPDATE_PRODUCT)) {
      throw new Error('Permission denied: CREATE_PRODUCT');
    }

    const url = `${this.baseUrl}/products`;
    try {
      const response = await axios.post(url, {
        internal_name: product.internal_name,
        display_name: product.display_name,
        description: product.description,
      });
      return response.data;
    } catch (error) {
      console.error('Error creating product:', error);
      throw error;
    }
  }

  async getSamplesMetadataBySampleSetId(
    sampleSetId: string,
    type: string = 'user'
  ): Promise<Record<string, Record<string, any>>> {
    let url = this.protectLevel(
      `${this.baseUrl}/samples/sample_set/${sampleSetId}/metadata`,
      READ_PLATFORM_SAMPLE_SETS,
      READ_ORGANIZATION_SAMPLE_SETS
    );
    url += `?type=${type}`;
    try {
      const response = await axios.get(url);
      return response.data;
    } catch (error) {
      console.error('Error fetching sample set sample metadata:', error);
      throw error;
    }
  }

  async getConfigs(): Promise<Config[]> {
    const url = `${this.baseUrl}/configs`;
    try {
      const response = await axios.get(url);
      return response.data;
    } catch (error) {
      console.error('Error fetching models:', error);
      throw error;
    }
  }

  async createConfig(config: CreateConfig): Promise<Config> {
    if (!this.hasPermission(CREATE_CONFIG)) {
      throw new Error('Permission denied: CREATE_CONFIG');
    }

    const url = `${this.baseUrl}/configs`;
    try {
      const response = await axios.post(url, {
        config_name: config.config_name,
        pipeline_config: config.pipeline_config,
        product_id: config.product_id,
        active: config.active,
      });
      return response.data;
    } catch (error) {
      console.error('Error creating config:', error);
      throw error;
    }
  }

  async updateConfig(
    config: Config,
    active?: boolean,
    productId?: string
  ): Promise<any> {
    if (!this.hasPermission(UPDATE_CONFIG)) {
      throw new Error('Permission denied: UPDATE_CONFIG');
    }

    const url = `${this.baseUrl}/configs/${config.id}`;
    try {
      const response = await axios.put(url, {
        active,
        product_id: productId,
      });
      return response.data;
    } catch (error) {
      console.error('Error updating config:', error);
      throw error;
    }
  }

  async getProductViewers(): Promise<ProductViewer[]> {
    if (!this.hasPermission(READ_PRODUCT_VIEWER)) {
      throw new Error('Permission denied: READ_PRODUCT_VIEWER');
    }

    const url = `${this.baseUrl}/products/viewer`;
    try {
      const response = await axios.get(url);
      return response.data;
    } catch (error) {
      console.error('Error fetching product viewers:', error);
      throw error;
    }
  }

  async postProductViewer(
    productId: string,
    organizationId: string
  ): Promise<any> {
    if (!this.hasPermission(UPDATE_PRODUCT_VIEWER)) {
      throw new Error('Permission denied: UPDATE_PRODUCT_VIEWER');
    }

    const url = `${this.baseUrl}/products/viewer`;
    try {
      const response = await axios.post(url, {
        product_id: productId,
        organization_id: organizationId,
      });
      return response.data;
    } catch (error) {
      console.error('Error updating product viewers:', error);
      throw error;
    }
  }

  async deleteProductViewer(
    productId: string,
    organizationId: string
  ): Promise<any> {
    if (!this.hasPermission(UPDATE_PRODUCT_VIEWER)) {
      throw new Error('Permission denied: UPDATE_PRODUCT_VIEWER');
    }

    const url = `${this.baseUrl}/products/${productId}/viewer/organization/${organizationId}`;
    try {
      const response = await axios.delete(url);
      return response.data;
    } catch (error) {
      console.error('Error updating product viewers:', error);
      throw error;
    }
  }

  async getModels(): Promise<Model[]> {
    if (!this.hasPermission(READ_MODEL)) {
      throw new Error('Permission denied: READ_MODEL');
    }

    const url = `${this.baseUrl}/models`;
    try {
      const response = await axios.get(url);
      return response.data;
    } catch (error) {
      console.error('Error fetching models:', error);
      throw error;
    }
  }

  async lookupConfig(
    model_id: string,
    analyte_sets: string[],
    qc_analyte_sets: string[] = [],
    featurization_info: Record<string, any>
  ): Promise<Config | undefined> {
    if (!this.hasPermission(READ_CONFIG)) {
      throw new Error('Permission denied: READ_CONFIG');
    }

    const url = `${this.baseUrl}/configs/lookup`;
    try {
      const response = await axios.post(url, {
        model_id: model_id,
        analyte_sets: analyte_sets,
        qc_analyte_sets: qc_analyte_sets,
        featurization_info: featurization_info,
      });
      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error) && error.response?.status === 404) {
        return undefined;
      }
      console.error('Error looking up model config:', error);
      throw error;
    }
  }

  async getConfigByName(config_name: string): Promise<Config | undefined> {
    if (!this.hasPermission(READ_CONFIG)) {
      throw new Error('Permission denied: READ_CONFIG');
    }

    const url = `${this.baseUrl}/configs/name/${config_name}`;
    try {
      const response = await axios.get(url);
      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error) && error.response?.status === 404) {
        return undefined;
      }
      console.error('Error fetching config by name:', error);
      throw error;
    }
  }

  async saveSampleSetSampleMetadata(
    sampleSetId: string,
    metadata: Record<string, Record<string, string | number | boolean | null>>
  ): Promise<UpdateSampleMetadataResponse> {
    let url = this.protectLevel(
      `${this.baseUrl}/samples/sample_set/${sampleSetId}/metadata`,
      UPDATE_PLATFORM_SAMPLE_SET,
      UPDATE_ORGANIZATION_SAMPLE_SET
    );
    url += `?type=user`;
    try {
      const response = await axios.put<UpdateSampleMetadataResponse>(
        url,
        metadata
      );
      return response.data;
    } catch (error) {
      console.error('Error saving sample set sample metadata:', error);
      throw error;
    }
  }

  async saveSampleSetSampleUserMetadataFiles(
    sampleSetId: string,
    metadataFile: File,
    sampleColumn?: string
  ): Promise<UpdateSampleMetadataFileResponse> {
    let url = this.protectLevel(
      `${this.baseUrl}/samples/sample_set/${sampleSetId}/metadata/file`,
      UPDATE_PLATFORM_SAMPLE_SET,
      UPDATE_ORGANIZATION_SAMPLE_SET
    );
    if (sampleColumn !== undefined) {
      url += `?sample_column=${sampleColumn}`;
    }

    const form = new FormData();
    form.append('metadata_file', metadataFile, metadataFile.name);

    try {
      const response = await axios.put(url, form);
      return response.data;
    } catch (error) {
      console.error('Error saving sample set sample metadata:', error);
      throw error;
    }
  }

  // Analytic Configuration Snapshots
  async saveAnalyticConfigurationSnapshot(
    jobId: string,
    name: string,
    config: Record<string, any>
  ): Promise<AnalyticConfigurationSnapshot> {
    const url = this.protectLevel(
      `${this.baseUrl}/jobs/${jobId}/analytic-configuration-snapshots`,
      READ_PLATFORM_JOBS,
      READ_ORGANIZATION_JOBS
    );
    const request: CreateAnalyticConfigurationSnapshotRequest = {
      name,
      config,
    };

    try {
      const response = await axios.post<AnalyticConfigurationSnapshot>(
        url,
        request
      );
      return response.data;
    } catch (error) {
      console.error('Error saving analytic configuration snapshot:', error);
      throw error;
    }
  }

  async getAnalyticConfigurationSnapshots(
    jobId: string
  ): Promise<AnalyticConfigurationSnapshot[]> {
    const url = this.protectLevel(
      `${this.baseUrl}/jobs/${jobId}/analytic-configuration-snapshots`,
      READ_PLATFORM_JOBS,
      READ_ORGANIZATION_JOBS
    );

    try {
      const response = await axios.get<AnalyticConfigurationSnapshot[]>(url);
      return response.data;
    } catch (error) {
      console.error('Error fetching analytic configuration snapshots:', error);
      throw error;
    }
  }

  async updateAnalyticConfigurationSnapshot(
    snapshotId: string,
    config: Record<string, any>
  ): Promise<AnalyticConfigurationSnapshot> {
    const url = `${this.baseUrl}/analytic-configuration-snapshots/${snapshotId}`;
    const request: UpdateAnalyticConfigurationSnapshotRequest = { config };

    try {
      const response = await axios.put<AnalyticConfigurationSnapshot>(
        url,
        request
      );
      return response.data;
    } catch (error) {
      console.error('Error updating analytic configuration snapshot:', error);
      throw error;
    }
  }

  async deleteAnalyticConfigurationSnapshot(snapshotId: string): Promise<void> {
    const url = `${this.baseUrl}/analytic-configuration-snapshots/${snapshotId}`;

    try {
      await axios.delete(url);
    } catch (error) {
      console.error('Error deleting analytic configuration snapshot:', error);
      throw error;
    }
  }

  async grantAnalyticSnapshotOrgWideAccess(
    snapshotId: string
  ): Promise<{ message: string }> {
    const url = `${this.baseUrl}/analytic-configuration-snapshots/${snapshotId}/org-wide-access`;

    try {
      const response = await axios.post<{ message: string }>(url);
      return response.data;
    } catch (error) {
      console.error('Error granting org-wide access:', error);
      throw error;
    }
  }

  async revokeAnalyticSnapshotOrgWideAccess(
    snapshotId: string
  ): Promise<{ message: string }> {
    const url = `${this.baseUrl}/analytic-configuration-snapshots/${snapshotId}/org-wide-access`;

    try {
      const response = await axios.delete<{ message: string }>(url);
      return response.data;
    } catch (error) {
      console.error('Error revoking org-wide access:', error);
      throw error;
    }
  }

  async checkAnalyticSnapshotOrgWideAccess(
    snapshotId: string
  ): Promise<{ has_org_wide_access: boolean }> {
    const url = `${this.baseUrl}/analytic-configuration-snapshots/${snapshotId}/org-wide-access`;

    try {
      const response = await axios.get<{ has_org_wide_access: boolean }>(url);
      return response.data;
    } catch (error) {
      console.error('Error checking org-wide access:', error);
      throw error;
    }
  }

  async getMS2Spectra(
    jobId: string,
    sampleId: string,
    spectraId: string
  ): Promise<SpectraResponse[]> {
    const url = this.protectLevel(
      `${this.baseUrl}/inference_results/plots/job/${jobId}/sample/${sampleId}/spectra/${spectraId}/ms2_spectra`,
      READ_PLATFORM_JOBS,
      READ_ORGANIZATION_JOBS
    );
    try {
      const response = await axios.get<SpectraResponse[]>(url);
      return response.data;
    } catch (error) {
      console.error('Error fetching MS2 spectra:', error);
      throw error;
    }
  }

  async getMS2Xic(
    jobId: string,
    sampleId: string,
    analyteId: string
  ): Promise<any> {
    const url = this.protectLevel(
      `${this.baseUrl}/inference_results/plots/job/${jobId}/sample/${sampleId}/analyte/${analyteId}/ms2_xic`,
      READ_PLATFORM_INFERENCE_RESULT,
      READ_ORGANIZATION_INFERENCE_RESULT
    );
    try {
      const response = await axios.get(url);
      return response.data;
    } catch (error) {
      console.error('Error fetching MS2 XIC:', error);
      throw error;
    }
  }

  async getFeatureXic(
    jobId: string,
    sampleId: string,
    featureXicId: string
  ): Promise<any> {
    const url = this.protectLevel(
      `${this.baseUrl}/inference_results/plots/job/${jobId}/sample/${sampleId}/feature_xic/${featureXicId}`,
      READ_PLATFORM_INFERENCE_RESULT,
      READ_ORGANIZATION_INFERENCE_RESULT
    );
    try {
      const response = await axios.get(url);
      return response.data;
    } catch (error) {
      console.error('Error fetching feature XIC:', error);
      throw error;
    }
  }

  // Spectral Match Feedback Methods
  async createSpectralMatchFeedback(feedbackData: {
    job_id: string;
    sample_id: string;
    analyte_id: string;
    spectral_library_id: string;
    query_spectrum: Record<string, any>;
    vote: boolean; // true = thumbs up, false = thumbs down
    tags?: string[];
    comment?: string;
  }): Promise<{ id: string }> {
    const url = `${this.baseUrl}/spectral-match-feedback`;
    try {
      const response = await axios.post(url, feedbackData);
      return response.data;
    } catch (error) {
      console.error('Error creating spectral match feedback:', error);
      throw error;
    }
  }

  async updateSpectralMatchFeedback(
    feedbackId: string,
    updateData: {
      vote?: boolean;
      tags?: string[];
      comment?: string;
    }
  ): Promise<any> {
    const url = `${this.baseUrl}/spectral-match-feedback/${feedbackId}`;
    try {
      const response = await axios.put(url, updateData);
      return response.data;
    } catch (error) {
      console.error('Error updating spectral match feedback:', error);
      throw error;
    }
  }

  async getAnalyteFeedback(jobId: string, analyteId: string): Promise<any[]> {
    const url = `${this.baseUrl}/spectral-match-feedback/job/${jobId}/analyte/${analyteId}`;
    try {
      const response = await axios.get(url);
      return response.data;
    } catch (error) {
      console.error('Error fetching analyte feedback:', error);
      throw error;
    }
  }

  /**
   * List interpretive reports for a sample set at the caller's highest scope.
   * The user and organization variants list PUBLISHED only; the platform
   * variant additionally includes every status (incl. DRAFT).
   */
  async getSampleSetInterpretiveReports(
    sampleSetId: string,
    {
      skip = 0,
      limit = 10,
      orderField = 'last_updated',
      orderReverse = false,
    }: InterpretiveReportListOptions = {}
  ): Promise<SearchPaginatedResponse<InterpretiveReport>> {
    const url = this.protectLevel(
      `${this.baseUrl}/sample_sets/${sampleSetId}/interpretive_reports`,
      READ_PLATFORM_INTERPRETIVE_REPORTS,
      READ_ORGANIZATION_INTERPRETIVE_REPORTS
    );
    try {
      const response = await axios.get<
        SearchPaginatedResponse<InterpretiveReport>
      >(url, {
        params: {
          skip,
          limit,
          order_field: orderField,
          order_reverse: orderReverse,
        },
      });
      return response.data;
    } catch (error) {
      console.error('Error fetching sample set interpretive reports:', error);
      throw error;
    }
  }

  /** Fetch one interpretive report's DB record (not its built content). */
  async getInterpretiveReport(reportId: string): Promise<InterpretiveReport> {
    const url = this.protectLevel(
      `${this.baseUrl}/interpretive_reports/${reportId}`,
      READ_PLATFORM_INTERPRETIVE_REPORTS,
      READ_ORGANIZATION_INTERPRETIVE_REPORTS
    );
    try {
      const response = await axios.get<InterpretiveReport>(url);
      return response.data;
    } catch (error) {
      console.error('Error fetching interpretive report:', error);
      throw error;
    }
  }

  /**
   * Fetch the raw HTML of a report's active custom artifact.
   *
   * The endpoint 307-redirects to a short-lived presigned S3 URL; axios (the
   * browser) follows the redirect transparently. We read the body as text so
   * the viewer can mount the pre-built report HTML directly.
   */
  async getActiveReportArtifactHtml(reportId: string): Promise<string> {
    const url = this.protectLevel(
      `${this.baseUrl}/interpretive_reports/${reportId}/active_html`,
      READ_PLATFORM_INTERPRETIVE_REPORTS,
      READ_ORGANIZATION_INTERPRETIVE_REPORTS
    );
    try {
      const response = await axios.get<string>(url, { responseType: 'text' });
      return response.data;
    } catch (error) {
      console.error('Error fetching interpretive report artifact:', error);
      throw error;
    }
  }

  /**
   * One page of the caller's chats, newest activity first, in the standard
   * paginated shape. With `interpretationId` the page holds only chats bound
   * to that interpretation; without it, every chat of the caller.
   */
  async listChats(
    interpretationId?: string,
    skip = 0
  ): Promise<PaginatedResponse<InterpretationChat>> {
    const url = `${this.baseUrl}/chats`;
    try {
      const response = await axios.get<PaginatedResponse<InterpretationChat>>(
        url,
        {
          params: {
            ...(interpretationId !== undefined && {
              interpretation_id: interpretationId,
            }),
            skip,
          },
        }
      );
      return response.data;
    } catch (error) {
      console.error('Error listing chats:', error);
      throw error;
    }
  }

  /** One chat by id, as its creator. */
  async getChat(chatId: string): Promise<InterpretationChat> {
    // An empty id would turn this into the list request.
    if (!chatId) {
      throw new Error('A chat id is required to fetch a chat.');
    }
    const url = `${this.baseUrl}/chats/${chatId}`;
    try {
      const response = await axios.get<InterpretationChat>(url);
      return response.data;
    } catch (error) {
      console.error('Error fetching chat:', error);
      throw error;
    }
  }

  /**
   * Whole turns of a chat's committed messages, in chronological order. Pass the
   * `older_sequence` of the last page to fetch the turns before it.
   */
  async getChatMessages(
    chatId: string,
    sequence?: number
  ): Promise<ChatHistoryMessagesResponse> {
    if (!chatId) {
      throw new Error('A chat id is required to fetch chat messages.');
    }
    const url = `${this.baseUrl}/chats/${chatId}/messages`;
    try {
      const response = await axios.get<ChatHistoryMessagesResponse>(url, {
        params: { sequence },
      });
      return response.data;
    } catch (error) {
      console.error('Error fetching chat messages:', error);
      throw error;
    }
  }

  /**
   * Set a chat's title as its creator. The server treats a user title as
   * authoritative and never lets it change the chat's activity order.
   */
  async updateChatTitle(
    chatId: string,
    title: string
  ): Promise<InterpretationChat> {
    // An empty id would address the collection instead of one chat.
    if (!chatId) {
      throw new Error('A chat id is required to update a chat title.');
    }
    const url = `${this.baseUrl}/chats/${chatId}`;
    try {
      const response = await axios.patch<InterpretationChat>(url, { title });
      return response.data;
    } catch (error) {
      console.error('Error updating chat title:', error);
      throw error;
    }
  }

  /**
   * Create a chat grounded on the given interpretations.
   * The ID of the chat to be created must be a UUIDv4.
   */
  async createChat(
    interpretationIds: string[],
    chatId: string
  ): Promise<InterpretationChat> {
    if (!chatId) {
      throw new Error('A chat ID is required to create a chat.');
    }
    if (interpretationIds.some((id) => !id)) {
      throw new Error('Interpretation IDs must be non-empty.');
    }

    const url = `${this.baseUrl}/chats`;
    try {
      const response = await axios.post<InterpretationChat>(url, {
        interpretation_ids: interpretationIds,
        id: chatId,
      });
      return response.data;
    } catch (error) {
      console.error('Error creating chat:', error);
      throw error;
    }
  }

  /** Resolve one announced file to its presigned download. */
  async fetchChatAttachmentDownload(
    chatId: string,
    path: string
  ): Promise<ResultFileDownload> {
    // Per segment, because encodeURIComponent would escape the separators too.
    const encodedPath = path.split('/').map(encodeURIComponent).join('/');
    try {
      const response = await axios.get<ResultFileDownloadsResponse>(
        `${this.baseUrl}/chats/${chatId}/attachment/${encodedPath}`
      );
      const [download] = response.data.files;
      if (!download) {
        throw new Error(`No download was presigned for ${path}`);
      }
      return download;
    } catch (error) {
      console.error('Error resolving chat attachment download:', error);
      throw error;
    }
  }

  /**
   * Read a presigned download's bytes. The signed URL carries its own access,
   * so the request goes out without a bearer.
   */
  private async readPresignedDownload(
    download: ResultFileDownload
  ): Promise<Response> {
    const response = await fetch(download.download_url);
    if (!response.ok) {
      throw new Error(
        `Reading ${download.filename} failed: ${response.status} ${response.statusText}`
      );
    }
    return response;
  }

  async fetchChatAttachmentAsText(
    chatId: string,
    path: string
  ): Promise<string> {
    try {
      const response = await this.readPresignedDownload(
        await this.fetchChatAttachmentDownload(chatId, path)
      );
      return await response.text();
    } catch (error) {
      console.error('Error fetching chat attachment text:', error);
      throw error;
    }
  }

  async fetchChatAttachmentAsJSON(
    chatId: string,
    path: string
  ): Promise<Record<string, unknown>> {
    try {
      const response = await this.readPresignedDownload(
        await this.fetchChatAttachmentDownload(chatId, path)
      );
      return (await response.json()) as Record<string, unknown>;
    } catch (error) {
      console.error('Error fetching chat attachment JSON:', error);
      throw error;
    }
  }

  /** Save a presigned url file download */
  async savePresignedDownload(download: ResultFileDownload): Promise<void> {
    try {
      const response = await this.readPresignedDownload(download);
      saveBlob(await response.blob(), download.filename);
    } catch (error) {
      console.error('Error saving chat attachment:', error);
      throw error;
    }
  }
}

// Custom hook that creates and returns the API client
export const useNovaApi = () => {
  const { hasPermission } = useUser();

  // Use useMemo to prevent unnecessary re-creation of the client
  const client = useMemo(() => {
    return new NovaApiClient(hasPermission);
  }, [hasPermission]);

  return client;
};
